import { isEqual } from 'lodash-es';
import type { jsoneData } from '../../api/interfaces';

/**
 * Rule sources reference other elements by wrapping their id in a pair of `$`,
 * e.g. `$widget_1$ > 0`. This pulls the ids out of the pairs.
 *
 * Only *paired* markers count: a trailing unmatched `$` is ignored, which is
 * what keeps a half-typed rule in the builder from producing a garbage id.
 */
export function extractRuleIds(source: string): string[] {
  const indexes: number[] = [];
  for (const match of source.matchAll(/\$/g)) {
    indexes.push(match.index);
  }

  const ids: string[] = [];
  for (let i = 0; i < indexes.length - 1; i += 2) {
    ids.push(source.substring(indexes[i] + 1, indexes[i + 1]));
  }

  return ids;
}

/**
 * Compiles a rule source (a `showIfRule` or an `autocompleteRule`) into the
 * json-e payload the evaluator consumes.
 *
 * `initialValue` is the rule's value before it has ever been evaluated:
 * `false` for visibility rules (a rule that has not run yet must not reveal
 * anything) and `''` for computation rules.
 */
export function compileRule(
  source: string,
  relatedElement: string | number,
  initialValue: unknown,
): jsoneData {
  const context: { [key: string]: unknown } = {};
  for (const id of extractRuleIds(source)) {
    context[id.replaceAll('.', '_')] = id;
  }

  return {
    context,
    // `$` becomes a space so the markers stop being part of the expression,
    // and `.` becomes `_` so ids stay single json-e identifiers.
    template: { $eval: source.replaceAll('$', ' ').replaceAll('.', '_') },
    relatedElement,
    value: initialValue,
  };
}

/** A rule whose value changed during an {@link evaluateRules} pass. */
export interface RuleUpdate {
  rule: jsoneData;
  value: unknown;
}

/** What one {@link evaluateRules} pass produced. */
export interface RuleEvaluation {
  /** The full rule list, with the changed entries replaced. */
  rules: jsoneData[];
  /** Only the entries that produced a value, in evaluation order. */
  updates: RuleUpdate[];
}

/**
 * Re-evaluates every rule whose inputs changed between two form values.
 *
 * A rule is skipped when the slice of the form it depends on is unchanged —
 * that is the only thing keeping every keystroke from re-running every rule in
 * the module. `force` bypasses the check for the first pass after load, where
 * there is no previous value to compare against.
 */
export function evaluateRules(
  rules: readonly jsoneData[],
  previousValue: Partial<{ [key: string]: unknown }>,
  currentValue: Partial<{ [key: string]: unknown }>,
  force: boolean,
  evaluate: (data: jsoneData) => unknown,
): RuleEvaluation {
  const next = [...rules];
  const updates: RuleUpdate[] = [];

  for (let i = 0; i < next.length; i++) {
    const rule = next[i];
    const previousContext = contextFrom(rule, previousValue);
    const currentContext = contextFrom(rule, currentValue);

    if (!force && isEqual(previousContext, currentContext)) continue;

    const value = evaluate({
      context: currentContext,
      relatedElement: rule.relatedElement,
      template: rule.template,
      value: '',
    });
    if (value === undefined) continue;

    next[i] = { ...rule, value };
    updates.push({ rule: next[i], value });
  }

  return { rules: next, updates };
}

/** Projects the form value down to just the keys this rule reads. */
function contextFrom(
  rule: jsoneData,
  formValue: Partial<{ [key: string]: unknown }>,
): { [key: string]: unknown } {
  const context: { [key: string]: unknown } = {};
  for (const [key, sourceId] of Object.entries(rule.context)) {
    context[key] = formValue[sourceId as string];
  }
  return context;
}
